import { useState, useEffect, useCallback } from 'react';
import { DeckSummary } from '../types';
import { deckService } from '../services/deckService';
import { useAuth } from '../context/AuthContext';

interface UseDecksOptions {
  limit?: number;
  sortBy?: 'uploadedAt' | 'fileName';
  sortOrder?: 'asc' | 'desc';
  status?: string;
  searchQuery?: string;
}

export const useDecks = (options: UseDecksOptions = {}) => {
  const { token } = useAuth();
  const [decks, setDecks] = useState<DeckSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  const fetchDecks = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const result = await deckService.getDecks(
        {
          limit: options.limit || 20,
          offset,
          sortBy: options.sortBy,
          sortOrder: options.sortOrder,
          status: options.status,
          search: options.searchQuery,
        },
        token
      );

      setDecks(result.decks);
      setTotal(result.total);
      setHasMore(offset + result.decks.length < result.total);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [token, options.limit, options.sortBy, options.sortOrder, options.status, options.searchQuery, offset]);

  const deleteDeck = useCallback(async (deckId: string) => {
    if (!token) return;

    // Set deleting state immediately
    setDeleting(true);
    try {
      await deckService.deleteDeck(deckId, token);
      // Refresh the list
      await fetchDecks();
    } catch (err) {
      throw err;
    } finally {
      setDeleting(false);
    }
  }, [token, fetchDecks]);

  const refresh = useCallback(() => {
    setOffset(0);
    fetchDecks();
  }, [fetchDecks]);

  useEffect(() => {
    fetchDecks();
  }, [fetchDecks]);

  return { decks, loading, deleting, error, total, hasMore, deleteDeck, refresh, setOffset };
};

